import React, { useContext, useState } from 'react';
import {
    Box, TextField, Button, Typography, CircularProgress,
    Link, InputAdornment, IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import AuthService from '../services/AuthService';
import Roles from '../config/roles';
import AuthShell from './auth/AuthShell';
import { underlineStyle, primaryButtonSx, errorBoxSx } from './auth/authStyles';

const RegisterPage = () => {
    const { t, i18n } = useTranslation();
    const { setAuthState } = useContext(AuthContext);
    const navigate = useNavigate();

    const [form, setForm] = useState({
        firstName: '', lastName: '', email: '', username: '', companyName: '', password: '', confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
        if (serverError) setServerError(null);
    };

    const validate = () => {
        const errs = {};
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!form.firstName.trim()) errs.firstName = t('register.validation.firstNameRequired');
        if (!form.lastName.trim()) errs.lastName = t('register.validation.lastNameRequired');
        if (!form.email.trim()) { errs.email = t('register.validation.emailRequired'); }
        else if (!emailPattern.test(form.email)) { errs.email = t('register.validation.emailInvalid'); }
        if (!form.username.trim()) errs.username = t('register.validation.usernameRequired');
        if (!form.password) { errs.password = t('register.validation.passwordRequired'); }
        else if (form.password.length < 8) { errs.password = t('register.validation.passwordTooShort'); }
        if (!form.confirmPassword) { errs.confirmPassword = t('register.validation.confirmPasswordRequired'); }
        else if (form.password !== form.confirmPassword) { errs.confirmPassword = t('register.validation.passwordsMustMatch'); }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        setServerError(null);
        try {
            const data = await AuthService.register({
                firstName: form.firstName,
                lastName: form.lastName,
                email: form.email,
                username: form.username,
                companyName: form.companyName.trim() || undefined,
                password: form.password,
            });
            if (data?.jwtToken) {
                localStorage.setItem('LOGGED_IN', 'true');
                localStorage.setItem('JWT_TOKEN', data.jwtToken);
                localStorage.setItem('ROLE', Roles[data.role]);
                if (data.languageCode) i18n.changeLanguage(data.languageCode.toLowerCase());
                setAuthState({ isLoggedIn: true, user: data, authToken: data.jwtToken });
                navigate('/pricing');
            } else {
                navigate('/login');
            }
        } catch {
            setServerError(t('register.errorGeneric'));
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSubmit();
    };

    return (
        <AuthShell formMaxWidth={400}>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>
                {t('register.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 3 }}>
                {t('register.subtitle')}
            </Typography>

            {/* Server error */}
            {serverError && (
                <Box sx={{ ...errorBoxSx, animation: 'fadeSlideIn 0.2s ease both' }}>
                    <Typography variant="body2" color="error">{serverError}</Typography>
                </Box>
            )}

            {/* First + Last name */}
            <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                    fullWidth variant="standard"
                    label={t('register.firstName')} name="firstName"
                    value={form.firstName} onChange={handleChange} onKeyDown={handleKeyDown}
                    error={!!errors.firstName} helperText={errors.firstName || ' '}
                    sx={underlineStyle}
                />
                <TextField
                    fullWidth variant="standard"
                    label={t('register.lastName')} name="lastName"
                    value={form.lastName} onChange={handleChange} onKeyDown={handleKeyDown}
                    error={!!errors.lastName} helperText={errors.lastName || ' '}
                    sx={underlineStyle}
                />
            </Box>

            <TextField
                fullWidth variant="standard"
                label={t('register.email')} name="email" type="email"
                value={form.email} onChange={handleChange} onKeyDown={handleKeyDown}
                error={!!errors.email} helperText={errors.email || ' '}
                sx={underlineStyle}
            />

            <TextField
                fullWidth variant="standard"
                label={t('register.username')} name="username"
                value={form.username} onChange={handleChange} onKeyDown={handleKeyDown}
                error={!!errors.username} helperText={errors.username || ' '}
                sx={underlineStyle}
            />

            <TextField
                fullWidth variant="standard"
                label={t('register.companyName')} name="companyName"
                value={form.companyName} onChange={handleChange} onKeyDown={handleKeyDown}
                helperText={t('register.companyNameHint')}
                sx={underlineStyle}
            />

            <TextField
                fullWidth variant="standard"
                label={t('register.password')} name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password} onChange={handleChange} onKeyDown={handleKeyDown}
                error={!!errors.password} helperText={errors.password || ' '}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size="small" tabIndex={-1} onClick={() => setShowPassword(v => !v)} edge="end">
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={underlineStyle}
            />

            <TextField
                fullWidth variant="standard"
                label={t('register.confirmPassword')} name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                value={form.confirmPassword} onChange={handleChange} onKeyDown={handleKeyDown}
                error={!!errors.confirmPassword} helperText={errors.confirmPassword || ' '}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size="small" tabIndex={-1} onClick={() => setShowConfirm(v => !v)} edge="end">
                                {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{ ...underlineStyle, mb: 1 }}
            />

            <Button
                fullWidth variant="contained"
                disabled={loading}
                onClick={handleSubmit}
                sx={{ ...primaryButtonSx, mt: 2, mb: 1 }}
            >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('register.submit')}
            </Button>

            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: '#7f8c9a' }}>
                {t('register.alreadyHaveAccount')}{' '}
                <Link component={RouterLink} to="/login" sx={{ color: '#427898', fontWeight: 600 }}>
                    {t('register.signIn')}
                </Link>
            </Typography>
        </AuthShell>
    );
};

export default RegisterPage;
